import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Config from './pages/Config';
import Carros from './pages/Carros';
import Clientes from './pages/Clientes';
import Rotas from './pages/Rotas';
import Preventivas from './pages/Preventivas';
import NovaRota from './pages/NovaRota';
import RotaDetalhe from './pages/RotaDetalhe';
import MapaTecnicos from './pages/MapaTecnicos';
import Relatorios from './pages/Relatorios';
import RelatoriosManutencao from './pages/RelatoriosManutencao';
import RelatorioManutencaoDetalhe from './pages/RelatorioManutencaoDetalhe';
import Pesquisas from './pages/Pesquisas';
import PesquisaPublica from './pages/PesquisaPublica';
import RelatoriosManutencaoM8 from './pages/RelatoriosManutencaoM8';
import ComplementosMateriais from './pages/ComplementosMateriais';
import Usuarios from './pages/Usuarios';
import Funcoes from './pages/Funcoes';
import TiposServicoRota from './pages/TiposServicoRota';
import Estoque from './pages/Estoque';
import ReposicoesEstoque from './pages/ReposicoesEstoque';
import Perfil from './pages/Perfil';
import Layout from './components/Layout';
import { getHomePathForPermissoes } from './lib/permissions';
import './App.css';

const PATH_TO_PERMISSION = {
  '/': 'dashboard',
  '/rotas': 'rotas',
  '/rotas/nova': 'rotas',
  '/preventivas': 'preventivas',
  '/mapa-tecnicos': 'mapa_tecnicos',
  '/clientes': 'clientes',
  '/usuarios': 'usuarios',
  '/funcoes': 'funcoes',
  '/carros': 'carros',
  '/tipos-servico-rota': 'tipos_servico_rota',
  '/estoque': 'estoque',
  '/reposicoes': 'reposicoes',
  '/relatorios': 'relatorios',
  '/relatorios-manutencao': 'relatorios_manutencao',
  '/pesquisas': 'pesquisas',
  '/relatorios-manutencao-m8': 'relatorios_manutencao',
  '/complementos-materiais': 'complementos',
  '/config': 'config',
};

const getPermissionForPath = (pathname) => {
  if (PATH_TO_PERMISSION[pathname]) return PATH_TO_PERMISSION[pathname];
  if (pathname.startsWith('/rotas/')) return 'rotas';
  if (pathname.startsWith('/relatorios-manutencao/')) return 'relatorios_manutencao';
  return null;
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname, search } = useLocation();
  const permission = getPermissionForPath(pathname);
  const permissoes = user?.funcao?.permissoes;
  const hasAccess = !permissoes || permissoes.length === 0 || !permission || permissoes.includes(permission);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const next = `${pathname}${search || ''}`;
    const q = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/login${q}`} />;
  }

  if (!hasAccess) {
    const home = getHomePathForPermissoes(permissoes);
    // Evita loop: se a "home" também for a rota atual sem acesso, manda ao login
    if (home === pathname || home === '/login') {
      return <Navigate to="/login" replace />;
    }
    return <Navigate to={home} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/pesquisa/:token" element={<PesquisaPublica />} />
        <Route path="/" element={
          <PrivateRoute>
            <Layout><Dashboard /></Layout>
          </PrivateRoute>
        } />
        <Route path="/config" element={
          <PrivateRoute>
            <Layout><Config /></Layout>
          </PrivateRoute>
        } />
        <Route path="/perfil" element={
          <PrivateRoute>
            <Layout><Perfil /></Layout>
          </PrivateRoute>
        } />
        <Route path="/carros" element={
          <PrivateRoute>
            <Layout><Carros /></Layout>
          </PrivateRoute>
        } />
        <Route path="/estoque" element={
          <PrivateRoute>
            <Layout><Estoque /></Layout>
          </PrivateRoute>
        } />
        <Route path="/reposicoes" element={
          <PrivateRoute>
            <Layout><ReposicoesEstoque /></Layout>
          </PrivateRoute>
        } />
        <Route path="/usuarios" element={
          <PrivateRoute>
            <Layout><Usuarios /></Layout>
          </PrivateRoute>
        } />
        <Route path="/funcoes" element={
          <PrivateRoute>
            <Layout><Funcoes /></Layout>
          </PrivateRoute>
        } />
        <Route path="/tipos-servico-rota" element={
          <PrivateRoute>
            <Layout><TiposServicoRota /></Layout>
          </PrivateRoute>
        } />
        <Route path="/clientes" element={
          <PrivateRoute>
            <Layout><Clientes /></Layout>
          </PrivateRoute>
        } />
        <Route path="/rotas" element={
          <PrivateRoute>
            <Layout><Rotas /></Layout>
          </PrivateRoute>
        } />
        <Route path="/preventivas" element={
          <PrivateRoute>
            <Layout><Preventivas /></Layout>
          </PrivateRoute>
        } />
        <Route path="/rotas/nova" element={
          <PrivateRoute>
            <Layout><NovaRota /></Layout>
          </PrivateRoute>
        } />
        <Route path="/rotas/:id" element={
          <PrivateRoute>
            <Layout><RotaDetalhe /></Layout>
          </PrivateRoute>
        } />
        <Route path="/mapa-tecnicos" element={
          <PrivateRoute>
            <Layout><MapaTecnicos /></Layout>
          </PrivateRoute>
        } />
        <Route path="/relatorios" element={
          <PrivateRoute>
            <Layout><Relatorios /></Layout>
          </PrivateRoute>
        } />
        <Route path="/relatorios-manutencao" element={
          <PrivateRoute>
            <Layout><RelatoriosManutencao /></Layout>
          </PrivateRoute>
        } />
        <Route path="/relatorios-manutencao/:id" element={
          <PrivateRoute>
            <Layout><RelatorioManutencaoDetalhe /></Layout>
          </PrivateRoute>
        } />
        <Route path="/pesquisas" element={
          <PrivateRoute>
            <Layout><Pesquisas /></Layout>
          </PrivateRoute>
        } />
        <Route path="/relatorios-manutencao-m8" element={
          <PrivateRoute>
            <Layout><RelatoriosManutencaoM8 /></Layout>
          </PrivateRoute>
        } />
        <Route path="/complementos-materiais" element={
          <PrivateRoute>
            <Layout><ComplementosMateriais /></Layout>
          </PrivateRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
      <Toaster
        position="bottom-center"
        containerStyle={{
          bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
          zIndex: 9999,
        }}
        toastOptions={{
          style: { maxWidth: 'min(420px, 92vw)' },
          duration: 4000,
        }}
      />
    </AuthProvider>
  );
}

export default App;
